import { collection, doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import type { Role } from '@flintmint/core-shared/constants';
import type { UserRolesDoc } from '@flintmint/core-shared/firestore/schemas';
import { getFirestore } from './firebase-service';

const COLLECTION_NAME = 'userRoles';

/**
 * Live map of userId -> roles for the whole `userRoles` collection (small; one doc per user).
 * Returns the unsubscribe function.
 */
export const subscribeToUserRoles = (
  onData: (rolesByUserId: Map<string, Role[]>) => void,
  onError: (error: Error) => void
): (() => void) => {
  const firestore = getFirestore();
  return onSnapshot(
    collection(firestore, COLLECTION_NAME),
    (snapshot) => {
      onData(new Map(snapshot.docs.map((docSnap) => [docSnap.id, (docSnap.data() as UserRolesDoc).roles ?? []])));
    },
    onError
  );
};

/**
 * Full-doc overwrite of `userRoles/{userId}`. Revoking all roles writes `roles: []` — the doc is
 * NEVER deleted: the backend reads this doc on every session, and a missing doc on a web session is
 * first contact, so an accessList-matched account would be re-admitted with `['user']`.
 */
export const setUserRoles = async (userId: string, roles: Role[]): Promise<void> => {
  const firestore = getFirestore();
  const data: UserRolesDoc = { userId, roles, updatedAt: new Date().toISOString() };
  await setDoc(doc(firestore, COLLECTION_NAME, userId), data);
};

export const fetchUserRolesDoc = async (userId: string): Promise<UserRolesDoc | null> => {
  const firestore = getFirestore();
  const snapshot = await getDoc(doc(firestore, COLLECTION_NAME, userId));
  return snapshot.exists() ? (snapshot.data() as UserRolesDoc) : null;
};
